// DiscountToggle — global discount UI for the cart.
//
// One discount per transaction (not per-item, per the Phase 5 spec).
// Mode can be `percent` (% off) or `nominal` (flat IDR). The numeric input
// reuses a controlled value supplied by the parent cart panel.

import { Percent, DollarSign, X } from 'lucide-react';
import { cn, formatIDR, formatNumber } from '@/lib/format';
import type { DiscountType } from '@/types/domain';

export interface DiscountToggleProps {
  type: DiscountType;
  value: number;
  subtotal: number;
  onChange: (next: { type: DiscountType; value: number }) => void;
  onClear: () => void;
  disabled?: boolean;
}

export function DiscountToggle({
  type,
  value,
  subtotal,
  onChange,
  onClear,
  disabled = false,
}: DiscountToggleProps) {
  const hasValue = !!type && value > 0;

  // Nominal cannot exceed subtotal — clamp on input.
  const handleValueChange = (raw: string) => {
    if (!type) return;
    const numeric = Number(raw.replace(/[^0-9]/g, '')) || 0;
    let next = numeric;
    if (type === 'percent' && next > 100) next = 100;
    if (type === 'nominal' && next > subtotal) next = subtotal;
    onChange({ type, value: next });
  };

  const selectType = (next: DiscountType) => {
    if (disabled) return;
    onChange({ type: next, value: 0 });
  };

  return (
    <div className="space-y-2">
      {/* Label and mode selector share one row: the cart footer competes with
          the item list for height, so nothing here gets a line of its own. */}
      <div className="flex items-center justify-between gap-3">
        <p className="text-small font-medium text-ink shrink-0">
          Diskon
          {hasValue && (
            <button
              type="button"
              onClick={onClear}
              className="ml-2 text-caption font-normal text-ink-subtle hover:text-ink inline-flex items-center gap-0.5 align-middle"
              disabled={disabled}
              title="Hapus diskon"
            >
              <X size={12} />
              hapus
            </button>
          )}
        </p>
        <div className="inline-flex p-1 rounded bg-canvas border border-line">
        <button
          type="button"
          onClick={() => selectType('percent')}
          disabled={disabled}
          className={cn(
            'inline-flex items-center justify-center gap-1.5 px-2.5 py-1.5 rounded text-caption font-medium transition-colors',
            type === 'percent'
              ? 'bg-surface text-ink shadow-hover'
              : 'text-ink-subtle hover:text-ink',
          )}
        >
          <Percent size={12} />
          % disc
        </button>
        <button
          type="button"
          onClick={() => selectType('nominal')}
          disabled={disabled}
          className={cn(
            'inline-flex items-center justify-center gap-1.5 px-2.5 py-1.5 rounded text-caption font-medium transition-colors',
            type === 'nominal'
              ? 'bg-surface text-ink shadow-hover'
              : 'text-ink-subtle hover:text-ink',
          )}
        >
          <DollarSign size={12} />
          Nominal
        </button>
        <button
          type="button"
          onClick={() => selectType(null)}
          disabled={disabled}
          className={cn(
            'inline-flex items-center justify-center gap-1.5 px-2.5 py-1.5 rounded text-caption font-medium transition-colors',
            !type
              ? 'bg-surface text-ink shadow-hover'
              : 'text-ink-subtle hover:text-ink',
          )}
        >
          Tidak ada
        </button>
        </div>
      </div>

      {type && (
        <div className="flex items-stretch gap-2">
          <div className="relative flex-1">
            <span
              className={cn(
                'absolute top-1/2 -translate-y-1/2 text-neutral pointer-events-none',
                type === 'nominal' ? 'left-3' : 'right-3',
              )}
            >
              {type === 'nominal' ? 'Rp' : '%'}
            </span>
            <input
              type="text"
              inputMode="numeric"
              value={value > 0 ? formatNumber(value) : ''}
              placeholder={type === 'percent' ? '0' : '0'}
              onChange={(e) => handleValueChange(e.target.value)}
              disabled={disabled}
              className={cn(
                'input',
                type === 'nominal' ? 'pl-9 pr-3' : 'pl-3 pr-9',
              )}
            />
          </div>
          {type === 'nominal' && value > subtotal && (
            <p className="text-caption text-danger self-center">
              Max {formatIDR(subtotal)}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
